import { BufferAttribute, BufferGeometry, Vector3 } from 'three';

/**
 * Shared face → geometry helpers for the BSP importer. A `BrushFace` is a convex
 * world-space polygon plus its outward normal; both the brush builder
 * (`brushGeometry.ts`) and the displacement builder (`displacement.ts`) emit
 * these, and `facesToGeometry` triangulates them into one collision/visual mesh.
 * Kept in its own module so brush and displacement code can share it without an
 * import cycle.
 */
export interface BrushFace {
  /** Polygon vertices in WORLD space (Y-up), convex, in order. */
  verts: Vector3[];
  /** Outward face normal in WORLD space (for correct triangle winding). */
  normal: Vector3;
}

/** A Source plane normal (Z-up) as a world-space DIRECTION (no translation). */
export function srcDirToWorld(nx: number, ny: number, nz: number): Vector3 {
  return new Vector3(nx, nz, -ny);
}

/**
 * Triangulate faces (fan) into a non-indexed, world-space BufferGeometry, winding
 * each triangle so its geometric normal matches the outward face normal (three's
 * `Triangle.getNormal` convention — what `LevelCollider` reads for surf/ground
 * classification).
 */
export function facesToGeometry(faces: BrushFace[]): BufferGeometry {
  const positions: number[] = [];
  const ab = new Vector3();
  const cb = new Vector3();
  const triN = new Vector3();
  for (const face of faces) {
    const v = face.verts;
    for (let i = 1; i + 1 < v.length; i++) {
      const a = v[0] as Vector3;
      let b = v[i] as Vector3;
      let c = v[i + 1] as Vector3;
      cb.subVectors(c, b);
      ab.subVectors(a, b);
      triN.crossVectors(cb, ab);
      if (triN.dot(face.normal) < 0) {
        [b, c] = [c, b]; // flip so the collision/lighting normal points outward
      }
      positions.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
    }
  }
  const geom = new BufferGeometry();
  geom.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
  geom.computeVertexNormals();
  return geom;
}
